import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { AlertCircle, ChevronDown, ImagePlus, Loader2 } from "@digital-twins/design-system/icons";
import {
  ASSISTANT_IMAGE_CREDITS,
  AssistantImageQuality,
  creativeFormatForSize,
} from "@digital-twins/shared-types";
import type { CreativeFormatId } from "@digital-twins/shared-types";
import type { AssistantImageRequest } from "@digital-twins/shared-types/assistant";
import { CreditIcon } from "@/components/icons/CreditIcon";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Text } from "@/components/ui/Text";
import type {
  ImageApprovalBatchProgress,
  ThreadImageApprovalGroupProps,
} from "@/types/components/thread-image-approval";
import { imageApprovalPairs, imageRetryTarget, orderedImageFormats, requestedImageFormats } from "./imageApprovalGroups";
import { imageFormatsLabel, imageSizeLabel } from "@/utils/imageFormatCopy";
import { QuoteReferencePreviews } from "./QuoteReferencePreviews";
import { QuoteUsageWarnings } from "./QuoteUsageWarnings";
import { ImageCardCreativeFields } from "./ImageCardCreativeFields";
import { isSwap, sameSelection, selectionFromRequest } from "./imageCardSelection";
import { creativeLibraryApi } from "@/lib/api/creative-library";
import type { ImageCardPreview } from "@/types/creative-library";
import { ThreadImageFormatPicker } from "./ThreadImageFormatPicker";

const QUALITIES: Array<AssistantImageQuality> = ["medium", "high"];

function highestQuality(
  requests: Array<AssistantImageRequest>,
): AssistantImageQuality {
  return requests.reduce<AssistantImageQuality>(
    (best, request) =>
      ASSISTANT_IMAGE_CREDITS[request.arguments.quality] >
      ASSISTANT_IMAGE_CREDITS[best]
        ? request.arguments.quality
        : best,
    "medium",
  );
}

/**
 * One card per image request set: every image listed, one quality for the
 * whole set, one explicit approval. Prices always come from the shared table.
 */
export function ThreadImageApprovalGroup(props: ThreadImageApprovalGroupProps) {
  const { requests, disabled, batch, onGenerate, onTryAgain, onRequote, threadToken, messageToken, selection, onSelectionChange, results } =
    props;

  const { t } = useTranslation("app");
  const [chosenQuality, setChosenQuality] =
    useState<AssistantImageQuality | null>(null);
  const [chosenFormats, setChosenFormats] =
    useState<Array<CreativeFormatId> | null>(null);

  // The page owns the run: the card remounts when the thread appends the
  // approval's own question, and must pick the run back up from props.
  const progress = batch?.progress ?? null;
  const paused = batch?.paused ?? false;
  // Once a pair is admitted the set's quality is the one the server saved, never a stale tab's.
  const admittedRequest = requests.find(
    (request) =>
      request.executionStatus || request.formatExecutions?.length,
  );
  const lockedQuality = admittedRequest
    ? admittedRequest.arguments.quality
    : null;
  const quality =
    lockedQuality ?? chosenQuality ?? batch?.quality ?? highestQuality(requests);
  const unitCredits = ASSISTANT_IMAGE_CREDITS[quality];
  const hasRequote = requests.some((request) => request.requiresRequote);
  const sharedSize =
    new Set(requests.map((request) => request.arguments.size)).size === 1
      ? requests[0].arguments.size
      : null;
  const quotedFormat = hasRequote ? null : creativeFormatForSize(sharedSize);
  // The saved selection wins over this run's; turns from before formats made the quoted one.
  const isAdmitted = Boolean(admittedRequest);
  const savedFormats =
    requests.find((request) => request.selectedFormats?.length)
      ?.selectedFormats ??
    batch?.formats ??
    (isAdmitted && quotedFormat ? [quotedFormat.id] : null);
  const formats = quotedFormat
    ? (savedFormats ?? orderedImageFormats(chosenFormats ?? batch?.pickedFormats ?? requestedImageFormats(requests, quotedFormat.id), quotedFormat.id))
    : null;
  const pairs = imageApprovalPairs(requests, formats);
  const pending = pairs.filter(
    (pair) => !pair.status && !pair.request.requiresRequote,
  );
  const total = pending.length * unitCredits;
  const failedCount = pairs.filter((pair) => pair.status === "failed").length;
  const stoppedCount = pairs.filter((pair) => pair.status === "cancelled").length;
  const inFlightCount = pairs.filter(
    (pair) => pair.status === "queued" || pair.status === "running",
  ).length;
  const sharedSizeLabel = sharedSize ? imageSizeLabel(t, sharedSize) : null;
  const doneQuality = lockedQuality ?? batch?.quality ?? highestQuality(requests);
  const createdCount = pairs.filter(
    (pair) => pair.status === "completed",
  ).length;
  const isSettled = !progress && pending.length === 0 && inFlightCount === 0;
  // Until one image of the card exists, a click makes one image and the others wait for approval.
  const isAwaitingFirst =
    createdCount === 0 &&
    pairs.filter(
      (pair) =>
        pair.status !== "failed" &&
        pair.status !== "cancelled" &&
        !pair.request.requiresRequote,
    ).length > 1;

  // A card of one finished image has nothing to approve, so only a failure offers it.
  const retryTarget = onTryAgain && !hasRequote && !progress ? imageRetryTarget(requests, formats) : null;
  const retry = retryTarget && (pairs.length > 1 || !retryTarget.hasImage) ? retryTarget : null;
  // A price change takes the card off the list of formats: what it saved still says what is missing.
  const isRequoteStuck =
    hasRequote &&
    pending.length === 0 &&
    imageApprovalPairs(requests, requests.find((request) => request.selectedFormats?.length)?.selectedFormats ?? null).some((pair) => !pair.status);
  const triesShown = Math.max(1, ...requests.map((request) => request.imageTries ?? 1));
  const hasImage = createdCount > 0;
  // Nothing on the card can change what the rest render from, so only a new quote moves it on.
  const isContextStuck = Boolean(batch?.contextRefused) && hasImage && !retry && !progress && pending.length > 0;
  const canTryNewPick = Boolean(retry?.hasImage);
  // Once an image exists the rest follow it, so the card starts from what it rendered; only Try again reads a new pick.
  const shownRequest = requests.find((request) => request.imageBrandToken != null);
  const shownSelection = hasImage && shownRequest
    ? selectionFromRequest({ contextSummary: shownRequest.imageSummary ?? shownRequest.contextSummary })
    : null;
  const isShownRebuilt = hasImage && Boolean(shownRequest?.imageSummary);
  const targetRequest = retry?.request ?? pending[0]?.request ?? null;
  const quoted = targetRequest ? selectionFromRequest(targetRequest) : null;
  const current = selection ?? shownSelection ?? quoted;
  const showsFields = Boolean(onSelectionChange) && Boolean(current) && !progress && !hasRequote && inFlightCount === 0 &&
    (!hasImage || canTryNewPick) && (pending.length > 0 || Boolean(retry));
  const isEdited = Boolean(showsFields && quoted && current && !sameSelection(quoted, current));
  const isNewPick = Boolean(hasImage && shownSelection && current && !sameSelection(shownSelection, current));
  // Each row shows its own images, so only its labels decide this.
  const quotesDiffer = pending.some((pair) => quoted && !sameSelection({ ...selectionFromRequest(pair.request), referenceTokens: [] }, { ...quoted, referenceTokens: [] }));
  const quoteBlock = (targetRequest ?? pending[0]?.request)?.blockedReason ?? null;
  const [preview, setPreview] = useState<ImageCardPreview | null>(null);
  const [isChecking, setIsChecking] = useState(false);
  const [hasCheckFailed, setHasCheckFailed] = useState(false);
  const [checkRuns, setCheckRuns] = useState(0);
  // The server answers by the rules a click follows, so what shows is what the paid image uses.
  const needsPreview = (isEdited || (Boolean(quoteBlock) && Boolean(onSelectionChange)) || isShownRebuilt) && !progress && Boolean(threadToken && messageToken);
  const previewKey = needsPreview && current ? JSON.stringify(current) : null;
  const previewedRow = (request: AssistantImageRequest) => preview?.rows.find((row) => row.requestId === request.requestId);
  const isRunning = Boolean(progress) || inFlightCount > 0;
  useEffect(() => {
    // The click used what the card showed: it stands until the run ends.
    if (isRunning) {
      setIsChecking(false);
      return;
    }
    setPreview(null);
    setHasCheckFailed(false);
    if (!previewKey || !threadToken || !messageToken) {
      setIsChecking(false);
      return;
    }
    let isCurrent = true;
    setIsChecking(true);
    creativeLibraryApi
      .previewImageCard(threadToken, messageToken, JSON.parse(previewKey))
      .then((next) => {
        if (isCurrent) setPreview(next);
      })
      .catch(() => {
        if (isCurrent) setHasCheckFailed(true);
      })
      .finally(() => {
        if (isCurrent) setIsChecking(false);
      });
    return () => {
      isCurrent = false;
    };
  }, [previewKey, threadToken, messageToken, checkRuns, createdCount, isRunning]);
  const isCheckOpen = Boolean(previewKey) && (isChecking || hasCheckFailed || !preview);
  const tryError = retry ? previewedRow(retry.request)?.error ?? null : null;
  const generateError = pending.map((pair) => previewedRow(pair.request)?.error).find(Boolean) ?? (preview ? null : quoteBlock);
  const blockedReason = generateError ?? tryError;
  const cannotGenerate = isCheckOpen || Boolean(generateError);
  const cannotTry = isCheckOpen || Boolean(tryError);
  const isBriefRewritten = Boolean(showsFields && quoted && current && isSwap(quoted, current));
  // A row that will render, or the row Try again will make: what the click uses. A made image: what it used.
  const rowSummary = (request: AssistantImageRequest) => {
    const isNextTry = isNewPick && retry?.request.requestId === request.requestId;
    const isRowDone = pairs.some((pair) => pair.request.requestId === request.requestId && pair.status === "completed");
    const isRowRendering = pairs.some((pair) => pair.request.requestId === request.requestId && (pair.status === "queued" || pair.status === "running"));
    if (isRowRendering) {
      // What the click was shown stands: a try's own labels still read the image before it.
      return previewedRow(request)?.summary ?? request.imageSummary ?? request.contextSummary;
    }
    if (isRowDone && !isNextTry) {
      return request.imageSummary ?? request.contextSummary;
    }
    if (!previewKey && !(isRunning && preview)) {
      return request.contextSummary;
    }
    return previewedRow(request)?.summary ?? null;
  };

  const onTryAgainClick = () => {
    if (retry && !disabled && !cannotTry) {
      onTryAgain?.(retry, quality, formats, (retry.request.imageTries ?? 1) + 1, onSelectionChange && current ? current : undefined);
    }
  };

  const onGenerateAll = () => {
    if (progress || disabled || cannotGenerate || pending.length === 0) {
      return;
    }
    const step = pairs.length > 1 ? (createdCount === 0 ? "first" : "rest") : undefined;
    // The rest follow the image approved, so a choice on the card says nothing about them.
    // Unedited, each row keeps its own quote: a card whose rows differ never moves one onto another.
    const chosen = step !== "rest" && onSelectionChange
      ? selection ?? selectionFromRequest(pending[0].request)
      : undefined;
    onGenerate(createdCount === 0 ? pending.slice(0, 1) : pending, quality, formats, step, step === "rest" ? triesShown : undefined, chosen);
  };

  const creditsBadge = (credits: number) => (
    <span className="inline-flex items-center gap-1.5">
      <CreditIcon className="h-4 w-4" />
      {credits}
    </span>
  );

  const tryAgainButton = (
    <Button
      variant="outline"
      className="h-10 gap-2 rounded-xl"
      disabled={disabled || cannotTry}
      data-testid="image-approval-try-again"
      onClick={onTryAgainClick}
    >
      {t("threadImage.approval.tryAgain")}
      {creditsBadge(unitCredits)}
    </Button>
  );

  const generateLabel = () => {
    const count = pending.length;
    if (retry?.hasImage) {
      return t("threadImage.approval.approveRemaining", { count });
    }
    if (isAwaitingFirst) {
      return t("threadImage.approval.generateFirst", { count });
    }
    // Paused says the next click repeats an attempt; after a failed first, the next image was never tried.
    if (paused && !retry) {
      return t("threadImage.approval.retryImages", { count });
    }
    if (createdCount === 1) {
      return t("threadImage.approval.approveRemaining", { count });
    }
    return createdCount > 1
      ? t("threadImage.approval.generateRemainingImages", { count })
      : t("threadImage.approval.generateImages", { count });
  };

  const approveAction = (
    <div className="flex w-full sm:w-auto">
      <Button
        className="h-auto min-h-10 min-w-0 flex-1 gap-2 rounded-l-xl rounded-r-none px-3 py-1 sm:flex-none sm:gap-3 sm:px-4"
        disabled={disabled || cannotGenerate}
        data-testid="image-approval-generate"
        onClick={onGenerateAll}
      >
        <span className="whitespace-normal leading-tight">
          {generateLabel()}
        </span>
      </Button>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            className="h-auto min-h-10 shrink-0 gap-1 rounded-l-none rounded-r-xl border-l border-primary-foreground/20 px-2.5 sm:gap-1.5 sm:px-3"
            disabled={disabled || isAdmitted}
            aria-label={t("threadImage.approval.quality")}
            data-testid="image-approval-quality-trigger"
          >
            {t(`assistantProfile.${quality}`)}
            <span className="whitespace-nowrap">{creditsBadge(total)}</span>
            <ChevronDown className="h-4 w-4" aria-hidden />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuRadioGroup
            value={quality}
            onValueChange={(next) => {
              setChosenQuality(next as AssistantImageQuality);
            }}
          >
            {QUALITIES.map((option) => (
              <DropdownMenuRadioItem
                key={option}
                value={option}
                data-testid={`image-approval-quality-option-${option}`}
              >
                <span className="flex items-center gap-2">
                  {t(`assistantProfile.${option}`)}
                  <span className="inline-flex items-center gap-1 text-muted-foreground">
                    <CreditIcon className="h-3 w-3" />
                    {ASSISTANT_IMAGE_CREDITS[option]}
                    <span>{t("threadImage.approval.perImage")}</span>
                  </span>
                </span>
              </DropdownMenuRadioItem>
            ))}
          </DropdownMenuRadioGroup>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
  const actionsWithTry = (
    <div
      className="flex w-full flex-col gap-2 sm:w-auto sm:flex-row sm:flex-wrap sm:justify-end"
      data-testid="image-approval-actions"
    >
      {tryAgainButton}
      {approveAction}
    </div>
  );

  let footer;
  if (progress) {
    footer = (
      <Button
        className="w-full gap-2 rounded-xl sm:w-auto"
        disabled
        data-testid="image-approval-generate"
      >
        <Loader2
          className="h-4 w-4 animate-spin motion-reduce:animate-none"
          aria-hidden
        />
        {isAwaitingFirst
          ? t("threadImage.approval.creatingFirst")
          : t("threadImage.approval.creating", {
              current: progress.current,
              total: progress.total,
            })}
      </Button>
    );
  } else if (inFlightCount > 0) {
    footer = (
      <Button
        className="w-full gap-2 rounded-xl sm:w-auto"
        disabled
        data-testid="image-approval-generate"
      >
        <Loader2
          className="h-4 w-4 animate-spin motion-reduce:animate-none"
          aria-hidden
        />
        {t("threadImage.approval.inProgress")}
      </Button>
    );
  } else if (retry && !retry.hasImage) {
    footer = (
      <>
        <Text size="sm" color="muted" data-testid="image-approval-failed">
          {t(retry.status === "cancelled" ? "threadImage.approval.firstStopped" : "threadImage.approval.firstFailed")}
        </Text>
        {/* The next concept becomes the new first, so a first image that keeps failing strands nothing. */}
        {pending.length > 0 ? actionsWithTry : tryAgainButton}
      </>
    );
  } else if (isRequoteStuck || isContextStuck) {
    footer = (
      <>
        {isContextStuck ? (
          <Text size="sm" color="muted" data-testid="image-approval-context-refused">
            {t("threadImage.approval.contextRefused")}
          </Text>
        ) : (
          <Text size="sm" color="muted">
            {t("creativeLibrary.requoteRequired")}
          </Text>
        )}
        {onRequote && (
          <Button
            variant="outline"
            className="h-10 rounded-xl"
            disabled={disabled}
            data-testid="image-approval-requote"
            onClick={() => onRequote(isContextStuck ? "context" : undefined)}
          >
            {t("threadImage.approval.requoteAction")}
          </Button>
        )}
      </>
    );
  } else if (pending.length > 0) {
    footer = retry ? actionsWithTry : approveAction;
  } else if (failedCount > 0 || stoppedCount > 0) {
    footer = (
      <Text size="sm" color="muted" data-testid="image-approval-failed">
        {[
          failedCount > 0 ? t("threadImage.approval.failedSome", { count: failedCount }) : null,
          stoppedCount > 0 ? t("threadImage.approval.stoppedSome", { count: stoppedCount }) : null,
        ]
          .filter(Boolean)
          .join(" ")}
      </Text>
    );
  } else if (createdCount > 0) {
    footer = (
      <Text
        size="sm"
        color="muted"
        className="inline-flex items-center gap-1.5"
        data-testid="image-approval-done"
      >
        <span>{t("threadImage.approval.created", { count: createdCount })}</span>
        <span>·</span>
        <span>{t(`assistantProfile.${doneQuality}`)}</span>
      </Text>
    );
  } else if (hasRequote) {
    footer = (
      <Text size="sm" color="muted">
        {t("creativeLibrary.requoteRequired")}
      </Text>
    );
  }

  return (
    <div
      className="flex flex-col gap-4 rounded-2xl border border-primary/20 bg-background p-4 sm:p-5"
      data-testid="thread-image-approval"
    >
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-primary/15 text-primary">
          <ImagePlus className="h-5 w-5" aria-hidden />
        </div>
        <Text size="sm" className="min-w-48 flex-1 leading-5" data-testid="image-approval-header">
          {t("threadImage.approval.title")}
          {` · ${t("threadImage.approval.images", { count: pairs.length })}`}
          {sharedSizeLabel && !formats ? ` · ${sharedSizeLabel}` : ""}
        </Text>
      </div>
      {showsFields && current && (
        <ImageCardCreativeFields
          selection={current}
          disabled={disabled}
          onChange={(next) => onSelectionChange?.(next)}
          onLibraryChanged={() => setCheckRuns((count) => count + 1)}
          refreshKey={checkRuns}
          isForcedOpen={Boolean(blockedReason)}
        />
      )}
      <ol className="flex flex-col gap-3">
        {requests.map((request, index) => {
          const summary = rowSummary(request);
          // Only references with a visible thumbnail count; the edited image has none.
          const visibleReferenceCount = summary?.referenceTokens?.length ?? 0;
          const isRowStarted = pairs.some((pair) => pair.request.requestId === request.requestId && pair.status);
          // The fields above name the choices, unless the rows hold different ones.
          const contextLabels = !showsFields || isRowStarted || quotesDiffer ? [
            summary?.brandName,
            summary?.productName,
            summary?.subjectName,
            summary?.boardName,
          ].filter(Boolean) : [];
          return (
            <li
              key={request.requestId}
              className="flex min-w-0 gap-3"
              data-testid={`image-approval-row-${index}`}
            >
              <Text size="sm" color="muted" className="w-5 shrink-0">
                {index + 1}.
              </Text>
              <div className="flex min-w-0 flex-col gap-1">
                <Text size="sm" className="leading-5">
                  {request.imagePurpose ?? request.arguments.purpose}
                </Text>
                {contextLabels.length > 0 && (
                  <Text size="xs" color="muted" className="break-words">
                    {contextLabels.join(" · ")}
                  </Text>
                )}
                {(!sharedSize || visibleReferenceCount > 0) && (
                  <Text size="xs" color="muted">
                    {sharedSize ? null : imageSizeLabel(t, request.arguments.size)}
                    {!sharedSize && visibleReferenceCount > 0 ? " · " : null}
                    {visibleReferenceCount > 0
                      ? t("creativeLibrary.quoteReferences", {
                          count: visibleReferenceCount,
                        })
                      : null}
                  </Text>
                )}
                <QuoteReferencePreviews
                  tokens={summary?.referenceTokens ?? []}
                />
              </div>
            </li>
          );
        })}
      </ol>
      {results}
      <QuoteUsageWarnings
        requests={requests.map((request) => ({ ...request, contextSummary: rowSummary(request) }))}
      />
      {blockedReason && (
        <div role="status" className="flex items-start gap-2 text-muted-foreground" data-testid="image-approval-blocked">
          <AlertCircle className="h-4 w-4 shrink-0" aria-hidden />
          <Text size="xs" color="muted">
            {blockedReason}
          </Text>
          {generateError && onSelectionChange && !hasCheckFailed && (
            <button
              type="button"
              className="text-xs underline"
              data-testid="image-approval-check-retry"
              onClick={() => setCheckRuns((count) => count + 1)}
            >
              {t("threadImage.approval.checkRetry")}
            </button>
          )}
        </div>
      )}
      {hasCheckFailed && (
        <div role="status" className="flex items-center gap-2" data-testid="image-approval-check-failed">
          <Text size="xs" color="muted">
            {t("threadImage.approval.checkFailed")}
          </Text>
          <button
            type="button"
            className="text-xs underline"
            data-testid="image-approval-check-retry"
            onClick={() => setCheckRuns((count) => count + 1)}
          >
            {t("threadImage.approval.checkRetry")}
          </button>
        </div>
      )}
      {isBriefRewritten && !blockedReason && !hasCheckFailed && (
        <Text size="xs" color="muted" data-testid="image-approval-brief-note">
          {t("threadImage.approval.briefUpdates")}
        </Text>
      )}
      {isNewPick && showsFields && pending.length > 0 && (
        <Text size="xs" color="muted" data-testid="image-approval-next-try-note">
          {t("threadImage.approval.nextTryNote")}
        </Text>
      )}
      {(formats || footer) && (
        <div className="flex flex-col-reverse gap-2 sm:flex-row sm:flex-wrap sm:items-end sm:justify-between sm:gap-3">
          {formats && isSettled && (
            <Text size="sm" color="muted" data-testid="image-approval-formats">
              {imageFormatsLabel(t, formats)}
            </Text>
          )}
          {formats && !isSettled && (
            <ThreadImageFormatPicker
              selected={formats}
              rows={requests.length}
              disabled={disabled || Boolean(progress) || Boolean(savedFormats)}
              onChange={setChosenFormats}
            />
          )}
          {footer && (
            <div className="flex min-w-0 flex-col items-stretch gap-2 sm:ml-auto sm:items-end">
              {isAwaitingFirst && !retry && triesShown <= 1 && (
                <Text size="xs" color="muted" data-testid="image-approval-first-hint">
                  {t("threadImage.approval.firstImageHint")}
                </Text>
              )}
              {pending.length > 0 && createdCount > 0 && !progress && (
                <Text size="xs" color="muted" data-testid="image-approval-partial">
                  {t("threadImage.approval.partial", {
                    count: createdCount,
                    total: pairs.length,
                  })}
                </Text>
              )}
              {!savedFormats &&
                !progress &&
                (formats?.length ?? 0) >= 2 &&
                requests.length >= 2 && (
                  <Text
                    size="xs"
                    color="muted"
                    data-testid="image-approval-format-count"
                  >
                    {t("threadImage.approval.imagesInFormats", {
                      images: requests.length,
                      formats: formats?.length,
                    })}
                  </Text>
                )}
              {footer}
              {paused && pending.length > 0 && !(retry && !retry.hasImage) && (
                <div
                  role="status"
                  className="flex items-start gap-2 text-muted-foreground"
                >
                  <AlertCircle className="h-4 w-4 shrink-0" aria-hidden />
                  <Text size="xs" color="muted">
                    {t("threadImage.approval.batchPaused")}
                  </Text>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
